import { MenuProps as AriaMenuProps } from 'react-aria-components';
import React, { ReactNode, useEffect, useRef } from 'react';
import { Item } from 'react-stately/Item';
import { Section as DeprecatedSection } from 'react-stately/Section';
import { MenuProps } from './Menu';
import { MenuSection } from './MenuSection';
import { MenuItem } from './MenuItem';

/**
 * Considering the valid children of Menu in the implementation based on @react-stately/menu
 * was quite limited, traversing that and converting elements into the new RAC-based implementation
 * is doable, and avoids the need for dealing with breaking changes, until the next major version.
 */
export function useV5Compatibility<T extends object>(
  children: MenuProps<T>['children']
): AriaMenuProps<T>['children'] {
  const warnedRef = useRef(false);
  useEffect(() => {
    warnedRef.current = true;
  }, []);

  return mapChildren(children);

  function mapChildren<T extends object>(
    children: MenuProps<T>['children']
  ): AriaMenuProps<T>['children'] {
    if (typeof children === 'function') {
      return (node) => mapChildren(children(node));
    }
    return Array.isArray(children)
      ? React.Children.map(children, mapChild)
      : mapChild(children);
  }
  function mapChild(child: ReactNode): ReactNode {
    if (
      React.isValidElement(child) &&
      (child.type === DeprecatedSection ||
        child.type === Item ||
        child.type === MenuItem ||
        child.type === MenuSection)
    ) {
      const props = child.key
        ? { id: child.key.replace(/^\.\$/, ''), ...child.props }
        : child.props;
      if (
        !warnedRef.current &&
        child.key &&
        child.props.id === undefined &&
        child.props.onAction === undefined
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `Deprecation warning: ${child.type.name} with key ${child.key} is rendered without an id. In v6, onAction on Menu will be called based on the value of id prop only.`
        );
      }
      if (child.type === DeprecatedSection) {
        if (!warnedRef.current) {
          // eslint-disable-next-line no-console
          console.warn(
            `Deprecation warning: Using Section component in Menu is deprecated. Please use MenuSection component instead.`
          );
        }
        return React.createElement(
          MenuSection,
          { ...props, heading: props.title },
          mapChildren(props.children)
        );
      } else if (child.type === Item || child.type === MenuItem) {
        return React.createElement(MenuItem, props);
      } else if (child.type === MenuSection) {
        return React.cloneElement(child, props, mapChildren(props.children));
      }
    }
    return child;
  }
}

export const V5MenuCompatibilityContext = React.createContext<
  Pick<MenuProps<never>, 'dangerKeys'>
>({ dangerKeys: [] });
